import { Add, Delete, Edit, CreditCard } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import PaymentTypesService from '../../services/paymentTypes';

const emptyForm = {
  _id: null,
  name: '',
  description: '',
  applies_surcharge: false,
  installments: [],
};

const emptyInstallment = { quantity: 1, interest_rate: 0, label: '' };

function PaymentTypesAdmin({ storeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  const showSnack = (severity, message) => setSnack({ open: true, severity, message });

  // loadData sin signal: usado para refrescar tras guardar/eliminar
  const loadData = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const data = await PaymentTypesService.listByStore(storeId);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      showSnack('error', 'No se pudieron cargar los medios de pago.');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // Carga inicial con AbortController: en React.StrictMode (dev) el componente
  // se monta, desmonta y remonta. El cleanup aborta el primer request en vuelo
  // para que solo llegue uno al servidor.
  useEffect(() => {
    if (!storeId) return;
    const controller = new AbortController();

    const fetchInitial = async () => {
      setLoading(true);
      try {
        const data = await PaymentTypesService.listByStore(storeId, controller.signal);
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        showSnack('error', 'No se pudieron cargar los medios de pago.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchInitial();
    return () => controller.abort();
  }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpenForm(true);
  };

  const handleOpenEdit = (row) => {
    setErrors({});
    setForm({
      _id: row._id,
      name: row.name || '',
      description: row.description || '',
      applies_surcharge: !!row.applies_surcharge,
      installments: Array.isArray(row.installments) ? row.installments.map((i) => ({ ...i })) : [],
    });
    setOpenForm(true);
  };

  const updateInstallment = (index, key, value) => {
    setForm((prev) => {
      const next = [...prev.installments];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, installments: next };
    });
  };

  const addInstallment = () => {
    setForm((prev) => ({
      ...prev,
      installments: [...prev.installments, { ...emptyInstallment }],
    }));
  };

  const removeInstallment = (index) => {
    setForm((prev) => ({
      ...prev,
      installments: prev.installments.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = 'El nombre es requerido';
    if (form.applies_surcharge) {
      form.installments.forEach((it, idx) => {
        if (!it.quantity || Number(it.quantity) <= 0) e[`q_${idx}`] = 'Debe ser > 0';
        if (it.interest_rate === '' || Number(it.interest_rate) < 0) e[`i_${idx}`] = 'Debe ser ≥ 0';
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    description: form.description?.trim() || '',
    applies_surcharge: !!form.applies_surcharge,
    installments: form.applies_surcharge
      ? form.installments.map((it) => ({
          quantity: Number(it.quantity),
          interest_rate: Number(it.interest_rate),
          label: it.label?.trim() || `${it.quantity} cuota${it.quantity !== 1 ? 's' : ''}`,
        }))
      : [],
  });

  const handleSave = async () => {
    if (!storeId || !validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (form._id) {
        await PaymentTypesService.update(storeId, form._id, payload);
        showSnack('success', 'Medio de pago actualizado.');
      } else {
        await PaymentTypesService.create(storeId, payload);
        showSnack('success', 'Medio de pago creado.');
      }
      setOpenForm(false);
      resetForm();
      loadData();
    } catch {
      showSnack('error', 'No se pudo guardar. Revisá los datos e intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!storeId || !toDelete?._id) return;
    setDeleting(true);
    try {
      await PaymentTypesService.delete(storeId, toDelete._id);
      showSnack('success', 'Medio de pago eliminado.');
      setToDelete(null);
      loadData();
    } catch {
      showSnack('error', 'No se pudo eliminar.');
    } finally {
      setDeleting(false);
    }
  };

  if (!storeId) {
    return (
      <Box p={3}>
        <Alert severity="warning">No hay tienda seleccionada.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(120deg,#fafafa 0%,#f5f7fa 35%,#eef3f9 100%)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(6px)',
          px: 3,
          py: 2,
          zIndex: 2,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <CreditCard color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Medios de pago
          </Typography>
        </Stack>
        <Button startIcon={<Add />} variant="contained" onClick={handleOpenCreate}>
          Nuevo
        </Button>
      </Box>

      {loading && <LinearProgress />}

      {/* Table */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f5f7fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>¿Aplica recargo?</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cuotas configuradas</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CreditCard fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.applies_surcharge ? 'Sí' : 'No'}
                      color={row.applies_surcharge ? 'warning' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.installments?.length || 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleOpenEdit(row)} size="small" title="Editar">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => setToDelete(row)} size="small" color="error" title="Eliminar">
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Sin medios de pago configurados.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Create / Edit dialog */}
      <Dialog open={openForm} onClose={() => !saving && setOpenForm(false)} fullWidth maxWidth="md">
        <DialogTitle>{form._id ? 'Editar medio de pago' : 'Nuevo medio de pago'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <TextField
              label="Nombre"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              autoFocus
            />
            <TextField
              label="Descripción"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.applies_surcharge}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      applies_surcharge: e.target.checked,
                      installments: e.target.checked ? p.installments : [],
                    }))
                  }
                />
              }
              label="¿Aplica recargo con tarjeta?"
            />

            {form.applies_surcharge && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Cuotas</Typography>
                  <Button onClick={addInstallment} size="small" startIcon={<Add />}>
                    Agregar cuota
                  </Button>
                </Stack>
                <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#f5f7fa' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Cantidad</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Interés %</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Etiqueta</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Acción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {form.installments.map((it, idx) => (
                        <TableRow key={`ins-${idx}`}>
                          <TableCell sx={{ width: 110 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={it.quantity}
                              onChange={(e) => updateInstallment(idx, 'quantity', e.target.value)}
                              error={!!errors[`q_${idx}`]}
                              helperText={errors[`q_${idx}`]}
                              inputProps={{ min: 1 }}
                              sx={{ width: 90 }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 120 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={it.interest_rate}
                              onChange={(e) => updateInstallment(idx, 'interest_rate', e.target.value)}
                              error={!!errors[`i_${idx}`]}
                              helperText={errors[`i_${idx}`]}
                              inputProps={{ min: 0, step: 0.1 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={it.label || ''}
                              onChange={(e) => updateInstallment(idx, 'label', e.target.value)}
                              placeholder={`${it.quantity} cuota${it.quantity !== 1 ? 's' : ''}`}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton color="error" onClick={() => removeInstallment(idx)} size="small">
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {form.installments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Agregá al menos una cuota.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete dialog */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ¿Eliminar el medio de pago <strong>{toDelete?.name}</strong>? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} disabled={deleting}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PaymentTypesAdmin;
